import { FormFields } from '@src/components/FormFields';
import DairyQuery from '@src/QueryHooks/DairyQuery';
import { GlobalEl } from '@src/styles/app';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dropdown, Input } from 'semantic-ui-react';
import DairyService from '~/api/services/dairy';
import notify from '~/components/Notify';
import { LocationEl } from './style';
import Drawer from '~/components/Drawer';
import { handleErrors } from '~/utils/helpers';

interface ICreateEditLocationProps {
  isVisible: boolean;
  locationId?: string;
  handleHiddenDrawer: () => void;
  isLoading?: boolean;
}

const defaultValues = {
  name: '',
  type: '',
  officeEmail: '',
  streetName: '',
  streetNumber: null,
  city: '',
  zip: null,
  countryId: '',
  phonePrefix: '',
  phoneAreaCode: null,
  phoneNumber: '',
  telefaxPrefix: '',
  telefaxAreaCode: null,
  telefaxNumber: ''
}

const CreateEditLocation: React.FunctionComponent<ICreateEditLocationProps> = (props) => {
  const { isVisible, locationId, handleHiddenDrawer } = props;
  const { t } = useTranslation();

  const [isBusy, setIsBusy] = useState(false)

  const { handleSubmit, control, reset, setValue, watch, clearErrors } = useForm({
    mode: 'onSubmit'
  });

  const watchPhonePrefixField = watch('phonePrefix')
  const watchTelefaxPrefixField = watch('telefaxPrefix')
  const watchTelefaxNumber = watch('telefaxNumber')

  useEffect(() => {
    if (locationId && locationId !== 'create') {
      fetchLocationData()
    }
    else {
      reset(defaultValues)
    }
  }, [locationId])

  async function fetchLocationData() {
    setIsBusy(true)
    try {
      const response = await DairyService.getDairyLocation(locationId)
      reset(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  const {
    data: locationOptionData,
    refetch: fetchOptions,
    isLoading: isLoadingOptions
  } = DairyQuery.getLocationOptionData();

  useEffect(() => {
    fetchOptions()
  }, [t])

  const renderContent = () => {
    return (
      <>
        <FormFields.Input
          control={control}
          name={'name'}
          disableScroolIntoView
          rules={{
            required: {
              value: true,
              message: t('Please enter office name')
            },
            maxLength: {
              value: 100,
              message: t('Max char is 100')
            }
          }}
          placeholder={t('Office name')}
          label={t('Office name')}
        />
        <FormFields.Select
          control={control}
          disableScroolIntoView
          options={locationOptionData.officeTypes?.map(officeType => ({ value: officeType.key, text: officeType.label }))}
          name={'type'}
          label={t('Office type')}
          placeholder={t('Office type')}
          selectOnBlur={false}
          rules={{
            required: {
              value: true,
              message: t('Please select office type')
            }
          }}
        />
        <FormFields.Input
          control={control}
          disableScroolIntoView
          placeholder={t('Office email')}
          label={t('Office email')}
          name={'officeEmail'}
          rules={{
            required: {
              value: true,
              message: t('Please select office email')
            },
            pattern: {
              value: null,
              message: t('Please enter valid email')
            }
          }}
        />
        <FormFields.Input
          control={control}
          disableScroolIntoView
          rules={{
            required: {
              value: true,
              message: t('Please enter street name')
            },
            maxLength: {
              value: 100,
              message: t('Max char is 100')
            }
          }}
          name={'streetName'}
          placeholder={t('Street name')}
          label={t('Street name')}
        />
        <FormFields.Input
          control={control}
          disableScroolIntoView
          name={'streetNumber'}
          rules={{
            required: {
              value: true,
              message: t('Please enter street number')
            },
            maxLength: {
              value: 100,
              message: t('Max char is 100')
            }
          }}
          placeholder={t('Street number')}
          label={t('Street number')}
        />
        <FormFields.Input
          control={control}
          disableScroolIntoView
          name={'city'}
          rules={{
            required: {
              value: true,
              message: t('Please enter city')
            },
            maxLength: {
              value: 100,
              message: t('Max char is 100')
            }
          }}
          placeholder={t('City')}
          label={t('City')}
        />
        <FormFields.Input
          control={control}
          disableScroolIntoView
          name={'zip'}
          rules={{
            required: {
              value: true,
              message: t('Please enter zip code')
            },
            maxLength: {
              value: 10,
              message: t('Max char is 10')
            }
          }}
          placeholder={t('Zip Code')}
          label={t('Zip Code')}
        />
        <FormFields.Select
          control={control}
          selectOnBlur={false}
          disableScroolIntoView
          options={locationOptionData?.countries.map(country => ({ value: country.id, text: country.label, flag: country.iso }))}
          rules={{
            required: {
              value: true,
              message: t('Please please select country')
            }
          }}
          placeholder={t('Country')}
          name={'countryId'}
          label={t('Country')}
        />
        <FormFields.External
          name='phoneNumber'
          label={t('Phone number')}
          control={control}
          rules={{
            required: watchPhonePrefixField ? t('Please enter phone number') : false
          }}
        >
          <Input
            style={{ minWidth: 300 }}
            label={<Dropdown
              scrolling
              basic
              clearable
              options={locationOptionData?.countries.map(country => ({ value: country.id, text: country.phoneCode, flag: country.iso }))}
              selectOnBlur={false}
              onChange={(e, data) => {
                setValue('phonePrefix', data.value);
                if (!data.value) {
                  clearErrors('phoneNumber');
                }
              }}
              value={parseInt(watchPhonePrefixField) || ''}
              placeholder={t('Code')}
              name={'phonePrefix'}
            />}
            onChange={(e, data) => setValue('phoneNumber', data.value)}
            labelPosition='left'
            placeholder={watchPhonePrefixField ? t('Phone number') : t('Please select phone code')}
            name={'phoneNumber'}
          />
        </FormFields.External>
        <FormFields.External
          name='telefaxNumber'
          label={t('Telefax number')}
          control={control}
          rules={{
            required: watchTelefaxPrefixField ? t('Please enter telefax number') : false
          }}
        >
          <Input
            style={{ minWidth: 300 }}
            label={<Dropdown
              scrolling
              basic
              clearable
              options={locationOptionData?.countries.map(country => ({ value: country.id, text: country.phoneCode, flag: country.iso }))}
              selectOnBlur={false}
              onChange={(e, data) => {
                setValue('telefaxPrefix', data.value);
                if (!data.value) {
                  clearErrors('telefaxNumber');
                }
              }}
              value={parseInt(watchTelefaxPrefixField) || ''}
              placeholder={t('Code')}
              name={'telefaxPrefix'}
            />}
            value={watchTelefaxNumber}
            onChange={(e, data) => setValue('telefaxNumber', data.value)}
            labelPosition='left'
            placeholder={watchTelefaxPrefixField ? t('Telefax number') : t('Please select telefax code')}
            name={'telefaxNumber'}
          />
        </FormFields.External>
        <LocationEl.DrawerFooter>
          <GlobalEl.DefaultOutlineButton type='button' onClick={() => handleHiddenDrawer()}>
            {t('Cancel')}
          </GlobalEl.DefaultOutlineButton>
          <GlobalEl.PrimaryButton
          >{locationId === 'create' ? t('Add') : t('Update')}</GlobalEl.PrimaryButton>
        </LocationEl.DrawerFooter>
      </>
    );
  };

  async function onSubmit(data) {
    setIsBusy(true)
    try {
      if (locationId === 'create') {
        await DairyService.createLocation(data)
        notify(t('Location created'), 'success')
      } else {
        await DairyService.updateLocation(locationId, data)
        notify(t('Location updated'), 'success')
      }
      handleHiddenDrawer();
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  return <Drawer
    onHide={() => {
      reset();
      handleHiddenDrawer();
    }}
    handleSubmit={handleSubmit(onSubmit)}
    isBusy={isBusy || isLoadingOptions}
    header={locationId === 'create' ? t('Add new Location') : t('Edit Location')}
    content={locationOptionData && renderContent()}
    isVisible={isVisible}
    closeOnEscape
  />
};

export default CreateEditLocation;
